import * as XLSX from "xlsx";

// Generic table-export helpers. Callers describe their data via an
// ExportColumn array — one entry per column they want in the output. The
// `value` getter normalizes the row into a primitive the exporter can write
// directly (string / number / Date / null). When omitted, the row is indexed
// by `key` and stringified. Restricted-row handling, formatting, and any
// other display logic should be folded into `value` so the exported file
// matches what the user sees on screen.

export type ExportCellValue = string | number | Date | null | undefined;

export type ExportColumn<T> = {
  key: string;
  label: string;
  value?: (row: T) => ExportCellValue;
};

function getCell<T>(row: T, col: ExportColumn<T>): ExportCellValue {
  if (col.value) return col.value(row);
  const v = (row as Record<string, unknown>)[col.key];
  if (v === null || v === undefined) return null;
  if (typeof v === "string" || typeof v === "number") return v;
  if (v instanceof Date) return v;
  return String(v);
}

// RFC 4180 CSV escaping — wrap any field containing ", comma, or newline in
// quotes and double-up embedded quotes. Dates are emitted as ISO strings so
// Excel can parse them back to dates on import.
function escapeCsvCell(v: ExportCellValue): string {
  if (v === null || v === undefined) return "";
  const s = v instanceof Date ? v.toISOString().slice(0, 10) : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function timestampSuffix(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

export function exportToCsv<T>(filenameBase: string, columns: ExportColumn<T>[], rows: T[]): void {
  const header = columns.map((c) => escapeCsvCell(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => escapeCsvCell(getCell(r, c))).join(","))
    .join("\r\n");
  // Prepend BOM so Excel opens UTF-8 files with the right encoding by default.
  const csv = `﻿${header}\r\n${body}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, `${filenameBase}-${timestampSuffix()}.csv`);
}

export function exportToXlsx<T>(filenameBase: string, columns: ExportColumn<T>[], rows: T[]): void {
  const aoa: (string | number | Date | null)[][] = [
    columns.map((c) => c.label),
    ...rows.map((r) =>
      columns.map((c) => {
        const v = getCell(r, c);
        if (v === undefined) return null;
        return v;
      }),
    ),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Auto-size columns from the longest cell per column. Without this every
  // column ships at the default ~10-char width and users immediately resize.
  const colWidths = columns.map((c, idx) => {
    const headerLen = c.label.length;
    let max = headerLen;
    for (const row of rows) {
      const v = getCell(row, c);
      const s =
        v === null || v === undefined
          ? ""
          : v instanceof Date
            ? v.toISOString().slice(0, 10)
            : String(v);
      if (s.length > max) max = s.length;
      // Cap to keep one absurdly long cell from blowing out the layout.
      if (max >= 60) {
        max = 60;
        break;
      }
    }
    return { wch: Math.max(8, Math.min(max + 2, 60)), key: idx };
  });
  ws["!cols"] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, `${filenameBase}-${timestampSuffix()}.xlsx`);
}
